import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Users, Trash2, Edit2, Search, Eye } from "lucide-react";
import { Link } from "react-router-dom";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC"
];

const STATE_NAMES = {
  AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",
  CT:"Connecticut",DE:"Delaware",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",
  IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",
  ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",
  MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",
  NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",
  OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",
  TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",
  WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming",DC:"District of Columbia"
};

const emptyForm = {
  business_name: "",
  dba_name: "",
  contact_person: "",
  email: "",
  phone: "",
  billing_address_line1: "",
  billing_address_line2: "",
  billing_city: "",
  billing_state: "",
  billing_zipcode: "",
  shipping_address_line1: "",
  shipping_address_line2: "",
  shipping_city: "",
  shipping_state: "",
  shipping_zipcode: "",
  fein_number: "",
  sales_tax_number: "",
  tobacco_license_number: "",
  notes: ""
};

const CustomersPage = () => {
  const { currentOrg } = useOrg();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [copyBilling, setCopyBilling] = useState(false);
  const [form, setForm] = useState({...emptyForm});

  const fetchCustomers = async () => {
    if (!currentOrg) return;
    try {
      const response = await axios.get(`${API}/customers`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id },
        params: { search }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentOrg, search]);

  const resetForm = () => {
    setForm({...emptyForm});
    setEditingCustomer(null);
    setCopyBilling(false);
  };

  const handleCopyBilling = (checked) => {
    setCopyBilling(checked);
    if (checked) {
      setForm(prev => ({
        ...prev,
        shipping_address_line1: prev.billing_address_line1,
        shipping_address_line2: prev.billing_address_line2,
        shipping_city: prev.billing_city,
        shipping_state: prev.billing_state,
        shipping_zipcode: prev.billing_zipcode
      }));
    }
  };

  const updateBillingField = (field, value) => {
    const shippingField = field.replace('billing_', 'shipping_');
    setForm(prev => ({
      ...prev,
      [field]: value,
      ...(copyBilling ? { [shippingField]: value } : {})
    }));
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      business_name: customer.business_name || "",
      dba_name: customer.dba_name || "",
      contact_person: customer.contact_person || "",
      email: customer.email || "",
      phone: customer.phone || "",
      billing_address_line1: customer.billing_address_line1 || "",
      billing_address_line2: customer.billing_address_line2 || "",
      billing_city: customer.billing_city || "",
      billing_state: customer.billing_state || "",
      billing_zipcode: customer.billing_zipcode || "",
      shipping_address_line1: customer.shipping_address_line1 || "",
      shipping_address_line2: customer.shipping_address_line2 || "",
      shipping_city: customer.shipping_city || "",
      shipping_state: customer.shipping_state || "",
      shipping_zipcode: customer.shipping_zipcode || "",
      fein_number: customer.fein_number || "",
      sales_tax_number: customer.sales_tax_number || "",
      tobacco_license_number: customer.tobacco_license_number || "",
      notes: customer.notes || ""
    });
    setCopyBilling(false);
    setDialogOpen(true);
  };

  const handleView = async (customerId) => {
    try {
      const response = await axios.get(`${API}/customers/${customerId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      setViewingCustomer(response.data);
    } catch (error) {
      toast.error("Failed to load customer details");
    }
  };

  const buildFullAddress = (prefix) => {
    const parts = [
      form[`${prefix}_address_line1`],
      form[`${prefix}_address_line2`],
      [form[`${prefix}_city`], form[`${prefix}_state`], form[`${prefix}_zipcode`]].filter(Boolean).join(', ')
    ].filter(Boolean);
    return parts.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      billing_address: buildFullAddress('billing'),
      shipping_address: buildFullAddress('shipping')
    };
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.customer_id}`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, payload, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Customer created");
      }
      await fetchCustomers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (customerId) => {
    if (!confirm("Are you sure you want to delete this customer?")) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Customer deleted");
      await fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const formatAddress = (customer) => {
    if (customer.billing_address_line1) {
      const parts = [
        customer.billing_address_line1,
        customer.billing_address_line2,
        [customer.billing_city, customer.billing_state, customer.billing_zipcode].filter(Boolean).join(', ')
      ].filter(Boolean);
      return parts.join(', ');
    }
    return customer.billing_address || '-';
  };

  const renderAddressFields = (prefix, label, disabled = false) => {
    const updateField = (field, value) => {
      if (prefix === 'billing') {
        updateBillingField(field, value);
      } else {
        setForm(prev => ({...prev, [field]: value}));
      }
    };
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-zinc-700">{label}</h4>
        <div>
          <Label>Address Line 1</Label>
          <Input
            value={form[`${prefix}_address_line1`]}
            onChange={(e) => updateField(`${prefix}_address_line1`, e.target.value)}
            placeholder="Street address"
            disabled={disabled}
            data-testid={`${prefix}-address-line1`}
          />
        </div>
        <div>
          <Label>Address Line 2</Label>
          <Input
            value={form[`${prefix}_address_line2`]}
            onChange={(e) => updateField(`${prefix}_address_line2`, e.target.value)}
            placeholder="Suite, unit, building (optional)"
            disabled={disabled}
            data-testid={`${prefix}-address-line2`}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>City</Label>
            <Input
              value={form[`${prefix}_city`]}
              onChange={(e) => updateField(`${prefix}_city`, e.target.value)}
              placeholder="City"
              disabled={disabled}
              data-testid={`${prefix}-city`}
            />
          </div>
          <div>
            <Label>State</Label>
            <Select
              value={form[`${prefix}_state`] || "select_state"}
              onValueChange={(v) => updateField(`${prefix}_state`, v === "select_state" ? "" : v)}
              disabled={disabled}
            >
              <SelectTrigger data-testid={`${prefix}-state`}>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="select_state">Select state</SelectItem>
                {US_STATES.map(s => (
                  <SelectItem key={s} value={s}>{s} - {STATE_NAMES[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Zipcode</Label>
            <Input
              value={form[`${prefix}_zipcode`]}
              onChange={(e) => updateField(`${prefix}_zipcode`, e.target.value)}
              placeholder="ZIP"
              disabled={disabled}
              data-testid={`${prefix}-zipcode`}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Customers</h1>
          <p className="text-muted-foreground text-sm">Manage your customer database</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-customer-btn">
              <Plus className="w-4 h-4" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? "Edit Customer" : "Create Customer"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={form.business_name}
                    onChange={(e) => setForm({...form, business_name: e.target.value})}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dba_name">DBA Name</Label>
                  <Input
                    id="dba_name"
                    value={form.dba_name}
                    onChange={(e) => setForm({...form, dba_name: e.target.value})}
                    placeholder="Doing Business As (if any)"
                    data-testid="customer-dba-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={form.contact_person}
                    onChange={(e) => setForm({...form, contact_person: e.target.value})}
                    data-testid="customer-contact-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>

              {/* Billing Address */}
              <div className="border-t pt-4">
                {renderAddressFields("billing", "Billing Address")}
              </div>

              {/* Copy to Shipping */}
              <div className="flex items-center gap-2 border-t pt-4">
                <Checkbox
                  id="copy-billing"
                  checked={copyBilling}
                  onCheckedChange={handleCopyBilling}
                  data-testid="copy-billing-checkbox"
                />
                <Label htmlFor="copy-billing" className="text-sm font-normal cursor-pointer">
                  Same as billing address
                </Label>
              </div>

              {/* Shipping Address */}
              {renderAddressFields("shipping", "Shipping Address", copyBilling)}

              {/* Tax & License Numbers */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-zinc-700 mb-3">Tax & License Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="fein_number">FEIN Number</Label>
                    <Input
                      id="fein_number"
                      value={form.fein_number}
                      onChange={(e) => setForm({...form, fein_number: e.target.value})}
                      data-testid="customer-fein-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sales_tax_number">Sales Tax Number</Label>
                    <Input
                      id="sales_tax_number"
                      value={form.sales_tax_number}
                      onChange={(e) => setForm({...form, sales_tax_number: e.target.value})}
                      data-testid="customer-salestax-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="tobacco_license_number">Tobacco License</Label>
                    <Input
                      id="tobacco_license_number"
                      value={form.tobacco_license_number}
                      onChange={(e) => setForm({...form, tobacco_license_number: e.target.value})}
                      data-testid="customer-tobacco-input"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  rows={2}
                  data-testid="customer-notes-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="customer-submit-btn">
                  {editingCustomer ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search customers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="customer-search-input"
        />
      </div>

      {/* Customers Table */}
      <div className="bg-white border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="p-16 text-center">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Customer</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Contact</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Location</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Phone</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-muted-foreground font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.customer_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="py-3 px-4">
                      <p className="font-medium">{customer.business_name}</p>
                      {customer.dba_name && <p className="text-xs text-muted-foreground">DBA: {customer.dba_name}</p>}
                      {customer.email && <p className="text-xs text-muted-foreground">{customer.email}</p>}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{customer.contact_person || '-'}</td>
                    <td className="py-3 px-4 text-muted-foreground text-xs">
                      {customer.billing_city && customer.billing_state
                        ? `${customer.billing_city}, ${customer.billing_state}`
                        : '-'}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{customer.phone || '-'}</td>
                    <td className="py-3 px-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleView(customer.customer_id)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)} data-testid={`edit-customer-${customer.customer_id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.customer_id)} className="text-destructive hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer Detail Dialog */}
      <Dialog open={!!viewingCustomer} onOpenChange={() => setViewingCustomer(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingCustomer?.business_name}</DialogTitle>
          </DialogHeader>
          {viewingCustomer && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {viewingCustomer.dba_name && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">DBA Name</p>
                    <p className="font-medium">{viewingCustomer.dba_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Contact</p>
                  <p className="font-medium">{viewingCustomer.contact_person || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{viewingCustomer.email || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p className="font-medium">{viewingCustomer.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Outstanding Balance</p>
                  <p className="font-medium text-primary">${viewingCustomer.outstanding_balance?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Billing Address</p>
                  <p className="font-medium whitespace-pre-line">{formatAddress(viewingCustomer)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Shipping Address</p>
                  <p className="font-medium whitespace-pre-line">
                    {viewingCustomer.shipping_address_line1
                      ? [viewingCustomer.shipping_address_line1, viewingCustomer.shipping_address_line2, [viewingCustomer.shipping_city, viewingCustomer.shipping_state, viewingCustomer.shipping_zipcode].filter(Boolean).join(', ')].filter(Boolean).join(', ')
                      : viewingCustomer.shipping_address || '-'}
                  </p>
                </div>
              </div>

              {/* Tax Info */}
              {(viewingCustomer.fein_number || viewingCustomer.sales_tax_number || viewingCustomer.tobacco_license_number) && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm mb-2">Tax & License</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {viewingCustomer.fein_number && <div><p className="text-muted-foreground">FEIN</p><p className="font-medium">{viewingCustomer.fein_number}</p></div>}
                    {viewingCustomer.sales_tax_number && <div><p className="text-muted-foreground">Sales Tax</p><p className="font-medium">{viewingCustomer.sales_tax_number}</p></div>}
                    {viewingCustomer.tobacco_license_number && <div><p className="text-muted-foreground">Tobacco License</p><p className="font-medium">{viewingCustomer.tobacco_license_number}</p></div>}
                  </div>
                </div>
              )}

              {viewingCustomer.invoices?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Recent Invoices</h4>
                  <div className="space-y-2">
                    {viewingCustomer.invoices.slice(0, 5).map((inv) => (
                      <div key={inv.invoice_id} className="flex items-center justify-between p-3 bg-zinc-50">
                        <div>
                          <Link to={`/dashboard/invoices/${inv.invoice_id}`} className="text-primary hover:underline font-medium">
                            {inv.invoice_number}
                          </Link>
                          <p className="text-xs text-muted-foreground">{inv.invoice_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">${inv.grand_total?.toFixed(2)}</p>
                          <span className={`text-xs ${inv.status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {inv.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
